import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X } from 'lucide-react';

const GALLERY_ITEMS = [
  {
    id: 'hero-1', testId: 'gallery-card-hero-1',
    category: 'Minimalist Architecture',
    title: 'Aperture & Monolith',
    year: '2025', location: 'Valencia, Spain',
    camera: 'Hasselblad 907X • 45mm • f/4.0',
    image: 'https://images.unsplash.com/photo-1483366774565-c783b9f70e2c?w=1200&q=85&auto=format&fit=crop',
    description: 'Minimalist curved concrete balcony and soaring white architecture against pale atmospheric mist.',
    colSpan: 'md:col-span-12', height: 'h-[380px] md:h-[500px]',
  },
  {
    id: 'work-1', testId: 'gallery-card-work-1',
    category: 'Editorial Fashion',
    title: 'Silhouette in Noir',
    year: '2024', location: 'Paris, Studio 7e',
    camera: 'Leica M11 • 50mm Summilux • f/1.4',
    image: 'https://images.unsplash.com/photo-1606143412458-acc5f86de897?w=900&q=85&auto=format&fit=crop',
    description: 'Monochrome study of high fashion poise and subtle hands gesture.',
    colSpan: 'md:col-span-7', height: 'h-[480px] md:h-[560px]',
  },
  {
    id: 'work-2', testId: 'gallery-card-work-2',
    category: 'Minimalist Architecture',
    title: 'Two Cypresses & White Facade',
    year: '2025', location: 'Lisbon, Portugal',
    camera: 'Sony A7R V • 24-70mm GM II • f/8',
    image: 'https://images.unsplash.com/photo-1602128110234-2d11c0aaadfe?w=900&q=85&auto=format&fit=crop',
    description: 'Expansive pale courtyard with geometric white volumes and twin green trees under dawn sky.',
    colSpan: 'md:col-span-5', height: 'h-[380px] md:h-[560px]',
  },
  {
    id: 'work-3', testId: 'gallery-card-work-3',
    category: 'Cinematic Portraits',
    title: "The Weaver's Gaze",
    year: '2024', location: 'Oaxaca, Mexico',
    camera: 'Leica SL2 • 35mm APO • f/2.0',
    image: 'https://images.unsplash.com/photo-1544124094-8aea0374da93?w=900&q=85&auto=format&fit=crop',
    description: 'Intimate textured documentary portrait with natural atmospheric light and textile scarf.',
    colSpan: 'md:col-span-5', height: 'h-[420px] md:h-[540px]',
  },
  {
    id: 'work-4', testId: 'gallery-card-work-4',
    category: 'Editorial Fashion',
    title: 'Tulle & Silver Echo',
    year: '2025', location: 'Milan, Italy',
    camera: 'Hasselblad H6D-100c • 80mm • f/5.6',
    image: 'https://images.unsplash.com/photo-1601597565151-70c4020dc0e1?w=900&q=85&auto=format&fit=crop',
    description: 'Avant-garde editorial fashion sculpture and feathered textures.',
    colSpan: 'md:col-span-7', height: 'h-[420px] md:h-[540px]',
  },
  {
    id: 'work-5', testId: 'gallery-card-work-5',
    category: 'Minimalist Architecture',
    title: 'Angles of Clean Azure',
    year: '2024', location: 'Copenhagen, Denmark',
    camera: 'Leica M11 • 28mm Elmarit • f/5.6',
    image: 'https://images.pexels.com/photos/34133564/pexels-photo-34133564.jpeg?auto=compress&cs=tinysrgb&w=900',
    description: 'Crisp geometric clean white building edge cutting diagonally across clear daylight.',
    colSpan: 'md:col-span-8', height: 'h-[320px] md:h-[420px]',
  },
  {
    id: 'work-6', testId: 'gallery-card-work-6',
    category: 'Cinematic Portraits',
    title: 'The Gaze of Arles',
    year: '2024', location: 'Arles, France',
    camera: 'Leica M10-R • 50mm Noctilux • f/0.95',
    image: 'https://images.unsplash.com/photo-1506863530036-1efeddceb993?w=900&q=85&auto=format&fit=crop',
    description: 'Timeless monochrome portrait of artist in ambient studio shadow.',
    colSpan: 'md:col-span-4', height: 'h-[320px] md:h-[420px]',
  },
];

const FILTERS = [
  { key: 'All', label: 'All Works', testId: 'gallery-filter-all' },
  { key: 'Editorial', label: 'Editorial Fashion', testId: 'gallery-filter-editorial' },
  { key: 'Architecture', label: 'Architecture', testId: 'gallery-filter-architecture' },
  { key: 'Portraits', label: 'Cinematic Portraits', testId: 'gallery-filter-portrait' },
];

function matches(item, filter) {
  if (filter === 'All') return true;
  if (filter === 'Editorial') return item.category.includes('Editorial');
  if (filter === 'Architecture') return item.category.includes('Architecture');
  if (filter === 'Portraits') return item.category.includes('Portrait') || item.category.includes('Cinematic');
  return true;
}

function GalleryCard({ item, onOpen, index }) {
  return (
    <motion.div
      data-testid={item.testId}
      initial={{ opacity: 0, y: 50 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: '-60px' }}
      transition={{ duration: 0.85, ease: [0.16, 1, 0.3, 1], delay: (index % 3) * 0.12 }}
      className={`col-span-12 ${item.colSpan} group relative overflow-hidden cursor-pointer rounded-sm`}
      onClick={() => onOpen(item)}
    >
      <div className={`relative overflow-hidden ${item.height}`}>
        <img
          src={item.image}
          alt={item.title}
          className="w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-[1.04]"
          loading="lazy"
        />
        {/* Gradient overlay on hover */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-400" />

        {/* Corner film marks */}
        {['top-3 left-3 border-t border-l', 'top-3 right-3 border-t border-r', 'bottom-3 left-3 border-b border-l', 'bottom-3 right-3 border-b border-r'].map((cls, i) => (
          <div key={i} className={`absolute w-4 h-4 border-white/0 group-hover:border-white/50 transition-all duration-300 ${cls}`} />
        ))}

        {/* EXIF overlay */}
        <div className="absolute bottom-0 left-0 right-0 p-4 translate-y-2 opacity-0 group-hover:translate-y-0 group-hover:opacity-100 transition-all duration-400">
          <p className="text-[10px] font-mono uppercase tracking-widest text-white/60 mb-0.5">{item.location} · {item.year}</p>
          <p className="text-xs font-mono text-white/90 tracking-wide">{item.camera}</p>
        </div>

        {/* View label */}
        <div className="absolute top-4 right-4 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
          <span className="bg-white/90 text-[#121212] text-[10px] font-mono uppercase tracking-widest px-2.5 py-1 rounded-full">
            View
          </span>
        </div>
      </div>

      {/* Caption below */}
      <div className="pt-3 pb-1">
        <p className="text-[11px] font-mono uppercase tracking-widest text-neutral-400 mb-0.5">{item.category}</p>
        <p className="text-sm font-serif font-light text-[#121212]">{item.title}</p>
      </div>
    </motion.div>
  );
}

function Lightbox({ item, onClose }) {
  return (
    <motion.div
      data-testid="lightbox-modal"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.3 }}
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/88 backdrop-blur-sm p-4 md:p-8"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.92, opacity: 0, y: 20 }}
        animate={{ scale: 1, opacity: 1, y: 0 }}
        exit={{ scale: 0.92, opacity: 0, y: 20 }}
        transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
        className="relative max-w-5xl w-full bg-[#FDFCF7] rounded-2xl overflow-hidden shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          data-testid="lightbox-close-btn"
          onClick={onClose}
          className="absolute top-4 right-4 z-10 w-9 h-9 rounded-full bg-white/90 flex items-center justify-center shadow-md hover:bg-white transition-colors"
        >
          <X size={16} strokeWidth={1.5} className="text-[#121212]" />
        </button>

        <div className="grid grid-cols-1 md:grid-cols-[1fr_300px]">
          <img
            src={item.image}
            alt={item.title}
            className="w-full h-[50vh] md:h-[70vh] object-cover"
          />
          <div className="p-8 flex flex-col justify-between">
            <div>
              <p className="text-[10px] font-mono uppercase tracking-[0.25em] text-[#B88349] mb-2">{item.category}</p>
              <h3 className="font-serif text-2xl font-light text-[#121212] mb-3 leading-snug">{item.title}</h3>
              <p className="text-sm text-neutral-500 font-light leading-relaxed mb-6">{item.description}</p>
            </div>
            <div className="space-y-3 border-t border-black/[0.06] pt-5">
              {[
                ['Location', item.location],
                ['Year', item.year],
                ['Camera', item.camera],
              ].map(([label, value]) => (
                <div key={label}>
                  <p className="text-[10px] font-mono uppercase tracking-widest text-neutral-400">{label}</p>
                  <p className="text-xs font-light text-[#121212] mt-0.5">{value}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
}

export default function GalleryGrid({ filter, onFilterChange }) {
  const [selected, setSelected] = useState(null);
  const visible = GALLERY_ITEMS.filter(item => matches(item, filter));

  return (
    <section id="works" className="py-24 md:py-32 bg-[#FDFCF7]">
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        {/* Section header */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          className="flex flex-col sm:flex-row sm:items-end justify-between gap-6 mb-12"
        >
          <div>
            <p className="text-[11px] font-mono uppercase tracking-[0.28em] text-neutral-400 mb-2">Selected Works</p>
            <h2 className="font-serif text-3xl sm:text-4xl font-light text-[#121212] leading-tight">
              The Archive
            </h2>
          </div>

          {/* Filter Tabs */}
          <div className="flex flex-wrap gap-2">
            {FILTERS.map(({ key, label, testId }) => (
              <button
                key={key}
                data-testid={testId}
                onClick={() => onFilterChange(key)}
                className={`rounded-full px-4 py-1.5 text-[11px] font-mono uppercase tracking-widest border transition-all duration-300 ${
                  filter === key
                    ? 'bg-[#121212] text-white border-[#121212]'
                    : 'border-neutral-300 text-neutral-500 hover:border-neutral-700 hover:text-[#121212]'
                }`}
              >
                {label}
              </button>
            ))}
          </div>
        </motion.div>

        {/* Asymmetric Grid */}
        <AnimatePresence mode="wait">
          <motion.div
            key={filter}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.4 }}
            className="grid grid-cols-12 gap-4 md:gap-5"
          >
            {visible.map((item, i) => (
              <GalleryCard key={item.id} item={item} onOpen={setSelected} index={i} />
            ))}
          </motion.div>
        </AnimatePresence>

        {visible.length === 0 && (
          <div className="text-center py-20">
            <p className="text-neutral-400 font-light">No works in this category yet.</p>
          </div>
        )}
      </div>

      <AnimatePresence>
        {selected && <Lightbox item={selected} onClose={() => setSelected(null)} />}
      </AnimatePresence>
    </section>
  );
}
